// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import {
  getOverallLoginHistories,
  getTenantLoginHistories,
} from 'apis/login_histories';
import { Modal } from 'components/atoms/Modal';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { DATE_FORMAT } from 'util/ConstantValues';
import {
  LoginHistoryGetParams,
  OverallLoginHistory,
  TenantLoginHistory,
} from 'util/Types';
import styles from './CheckLoginStatusModal.module.scss';
import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import { useTranslation } from 'react-i18next';
import { SelectField } from 'components/molecules/SelectField';
import { Form } from 'react-final-form';
import { Bar } from 'react-chartjs-2';
import {
  forceBreakLine,
  formatNumberWithCommas,
  hashFnv1,
  monthConvertToEN,
} from 'util/commons';
import { labelSliceToDisplay } from 'util/dashboardTypes';

type CheckLoginStatusModalProps = {
  onClose?: () => void;
};

const MINIMUM_YEAR = 2025;

const CheckLoginStatusModal: React.FC<CheckLoginStatusModalProps> = ({
  onClose,
}) => {
  const user = useSelector(selectUser);
  const { t, i18n } = useTranslation();
  const currentYear = dayjs().year();

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedStartMonth, setSelectedStartMonth] = useState<number>(1);
  const [selectedEndMonth, setSelectedEndMonth] = useState<number>(
    dayjs().month() + 1
  );
  const [tenantLoginHistories, setTenantLoginHistories] = useState<
    TenantLoginHistory[]
  >([]);
  const [overallLoginHistories, setOverallLoginHistories] = useState<
    OverallLoginHistory[]
  >([]);

  useEffect(() => {
    if (!user.siteId) {
      return;
    }

    const selectedYearInstance = dayjs().year(selectedYear);

    const getParams: LoginHistoryGetParams = {
      siteId: user.siteId,
      dateFrom: selectedYearInstance
        .month(selectedStartMonth - 1)
        .startOf('month')
        .format(DATE_FORMAT.full_date),
      dateTo:
        selectedYear === currentYear && selectedEndMonth === dayjs().month() + 1
          ? dayjs().format(DATE_FORMAT.full_date)
          : selectedYearInstance
              .month(selectedEndMonth - 1)
              .endOf('month')
              .format(DATE_FORMAT.full_date),
    };

    getTenantLoginHistories({
      params: getParams,
      successCallback: (data) =>
        setTenantLoginHistories(data.filter(({ total }) => total > 0)),
    });
    getOverallLoginHistories({
      params: getParams,
      successCallback: setOverallLoginHistories,
    });
  }, [selectedYear, user.siteId, selectedStartMonth, selectedEndMonth]);

  const renderOverallLoginHistoriesChart = () => {
    if (!overallLoginHistories.length) {
      return (
        <div className={styles.chartEmpty}>
          {t('dashboard.empty_chart_data')}
        </div>
      );
    }

    const labels = Array.from(
      { length: selectedEndMonth + 1 - selectedStartMonth },
      (_, i) =>
        i18n.language === 'ja'
          ? `${selectedStartMonth + i}月`
          : monthConvertToEN(selectedStartMonth + i)
    );

    return (
      <Bar
        height={200}
        options={{
          indexAxis: 'x',
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                title: ([{ label }]) => `${label}/${selectedYear}`,
                label: (tooltipItem) => tooltipItem.formattedValue,
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              grid: {
                color: 'transparent',
                borderColor: '#6F6F6F',
                borderWidth: 1,
                drawBorder: true,
              },
              title: {
                display: false,
              },
              ticks: {
                color: '#CFCFCF',
              },
            },
            y: {
              suggestedMax:
                Math.max(
                  ...overallLoginHistories.map(({ total }) => total),
                  0
                ) * 1.1,
              grid: {
                color: '#6F6F6F',
                borderColor: '#6F6F6F',
                borderWidth: 1,
                drawBorder: true,
              },
              ticks: {
                color: '#CFCFCF',
                stepSize: 1,
                font: {
                  size: 12,
                },
              },
            },
          },
        }}
        data={{
          labels,
          datasets: [
            {
              data: labels.map(
                (_, i) =>
                  overallLoginHistories.find(
                    (h) =>
                      dayjs(h.loginDate).month() === selectedStartMonth - 1 + i
                  )?.total || 0
              ),
              backgroundColor: '#86EA5C',
              borderRadius: 4,
              maxBarThickness: 150,
              datalabels: {
                display: true,
                color: '#CFCFCF',
                anchor: 'end',
                align: 'end',
                formatter: (value) =>
                  value ? formatNumberWithCommas(value) : '',
              },
            },
          ],
        }}
      />
    );
  };

  const renderTenantLoginHistoriesChart = () => {
    if (!tenantLoginHistories.length) {
      return (
        <div className={styles.chartEmpty}>
          {t('dashboard.empty_chart_data')}
        </div>
      );
    }

    return (
      <Bar
        key={hashFnv1(tenantLoginHistories)} // Re-draw canvas when tenantLoginHistories changes
        height={Math.max(
          tenantLoginHistories.length *
            (tenantLoginHistories.length > 5 ? 8 : 12),
          28
        )}
        options={{
          indexAxis: 'y',
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                title: ([tooltipItem]) =>
                  tooltipItem?.dataIndex != null
                    ? forceBreakLine({
                        text: tenantLoginHistories[tooltipItem.dataIndex].name,
                        breakNode: 50,
                      })
                    : '',
                label: (tooltipItem) => tooltipItem.formattedValue,
              },
            },
          },
          responsive: true,
          scales: {
            x: {
              ticks: {
                color: '#CFCFCF',
                stepSize: 1,
              },
              grid: {
                color: '#6F6F6F',
                borderColor: '#6F6F6F',
                borderWidth: 1,
                drawBorder: true,
              },
            },
            y: {
              ticks: {
                color: '#CFCFCF',
                autoSkip: false,
              },
              grid: {
                color: 'transparent',
                borderColor: '#6F6F6F',
                borderWidth: 1,
                drawBorder: true,
              },
            },
          },
        }}
        data={{
          labels: tenantLoginHistories.map(({ name }) =>
            labelSliceToDisplay(name, 14)
          ),
          datasets: [
            {
              data: tenantLoginHistories.map(({ total }) => total),
              backgroundColor: '#86EA5C',
              borderRadius: 4,
              datalabels: {
                display: true,
                color: '#1B1B1B',
                formatter: (value) => formatNumberWithCommas(value),
              },
            },
          ],
        }}
      />
    );
  };

  const getMonthOptions = (type: 'start' | 'end') => {
    const monthCount =
      selectedYear === currentYear ? dayjs().get('month') + 1 : 12;

    let startIndex = 0;
    let endIndex = monthCount;

    if (type === 'start') {
      endIndex = selectedEndMonth;
    } else if (type === 'end') {
      startIndex = selectedStartMonth - 1;
    }

    return Array.from({ length: endIndex - startIndex }, (_, i) => {
      const monthIndex = startIndex + i;
      return {
        label:
          i18n.language === 'ja'
            ? `${monthIndex + 1}月`
            : monthConvertToEN(monthIndex + 1) || '',
        value: (monthIndex + 1).toString(),
      };
    });
  };

  return (
    <Modal centered className={styles.loginStatusModal}>
      <div className={styles.title}>
        <div className={styles.titleAction}>
          <p>{t('tenant_management.login_status_modal_title')}</p>
          <Form onSubmit={() => {}}>
            {() => (
              <>
                <SelectField
                  name="filter-year"
                  isDarkmode
                  value={selectedYear.toString()}
                  options={Array.from({
                    length: currentYear - MINIMUM_YEAR + 1,
                  }).map((_, i) => ({
                    label:
                      (MINIMUM_YEAR + i).toString() +
                      (i18n.language === 'ja' ? '年' : ''),
                    value: MINIMUM_YEAR + i,
                  }))}
                  onChange={(value) => {
                    const newYear = Number(value);
                    setSelectedYear(newYear);

                    setSelectedStartMonth(1);
                    const maxMonth =
                      newYear === currentYear ? dayjs().month() + 1 : 12;
                    setSelectedEndMonth(Math.min(selectedEndMonth, maxMonth));
                  }}
                />

                <SelectField
                  name="filter-start-month"
                  containerClassName={styles.selectMonth}
                  isDarkmode
                  value={selectedStartMonth.toString()}
                  options={getMonthOptions('start')}
                  onChange={(value) => {
                    const newStartMonth = Number(value);
                    setSelectedStartMonth(newStartMonth);

                    if (selectedEndMonth < newStartMonth) {
                      setSelectedEndMonth(newStartMonth);
                    }
                  }}
                />

                <span>~</span>

                <SelectField
                  name="filter-end-month"
                  containerClassName={styles.selectMonth}
                  isDarkmode
                  value={selectedEndMonth.toString()}
                  options={getMonthOptions('end')}
                  onChange={(value) => {
                    const newEndMonth = Number(value);
                    setSelectedEndMonth(newEndMonth);

                    if (selectedStartMonth > newEndMonth) {
                      setSelectedStartMonth(newEndMonth);
                    }
                  }}
                />
              </>
            )}
          </Form>
        </div>
        <IconCloseTablet onClick={onClose} />
      </div>

      <div className={styles.body}>
        <div className={styles.chartWrapper}>
          <p className={styles.chartTitle}>
            {t('tenant_management.login_per_month_chart')}
          </p>
          <div>{renderOverallLoginHistoriesChart()}</div>
        </div>

        <div className={styles.chartWrapper}>
          <p className={styles.chartTitle}>
            {t('tenant_management.login_per_tenant_chart')}
          </p>
          <div>{renderTenantLoginHistoriesChart()}</div>
        </div>
      </div>
    </Modal>
  );
};

export default CheckLoginStatusModal;
